/**
 * Catálogo de planes que se abre **al intentar generar sin créditos** — ADR-2026-07-30-copy-catalogo-segun-contexto-abrio.
 *
 * Reemplaza las dos pantallas viejas de `SubscriptionLimitModal`
 * (`free-trial-ended`, la de "30 imágenes gratis", y `plan-limit-reached`): ahora
 * el usuario ve directamente los planes, con el copy del momento
 * (`PRICING_COPY_SIN_CREDITOS`). Bottom sheet en mobile, modal en desktop.
 *
 * **Lo delicado de este componente son las ramas de cobro**, heredadas de las
 * pantallas que reemplaza. Se conservan enteras:
 *
 * - **Plan a medida** (`company_profiles.plan_custom_id`, ADR-664): NO ve el
 *   catálogo. Sus créditos se negocian, y desde el portal de Stripe podría
 *   pasarse a un plan público. Su salida es WhatsApp.
 * - **Mientras carga** no se muestra nada (ADR-668): con `plan_custom_id`
 *   todavía sin resolver, el catálogo le ofrecería un plan público a un cliente a
 *   medida durante esos milisegundos.
 * - **Si la consulta falla**, se cae a `/subscription` (ADR-668): esa pantalla
 *   resuelve el plan a medida en su propia consulta y falla cerrado.
 * - **Suscribirse** con una suscripción de Stripe activa va al **Billing Portal**,
 *   no al checkout (ADR-661): una Checkout Session nueva crearía una SEGUNDA
 *   suscripción y la vieja seguiría cobrando.
 *
 * Los precios salen de los helpers canónicos de `checkoutPlans` y del mismo cupón
 * invisible que `/subscription` (`lib/hotSaleCoupon`), así el usuario ve el mismo
 * precio por los dos caminos.
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { fetchStripeBillingPortalUrl } from '../../lib/paymentServer';
import { fetchDatosPlanCustom, type MiPlanCustom } from '../../lib/planesCustom';
import { fetchHotSaleCoupon, type AppliedCoupon } from '../../lib/hotSaleCoupon';
import {
  getPlanById,
  getPlanPriceForPais,
  formatPriceForPais,
} from '../../lib/checkoutPlans';
import ContactarPorWhatsApp from '../ContactarPorWhatsApp';
import PricingCatalog from './PricingCatalog';
import { getCarouselSlidesPorLabel } from '../onboarding/onboardingSlides';
import { buildPlanViews } from './buildPlanViews';
import { PLAN_ORDER, ULTRA_TIERS } from './pricingPlans';
import { RECOMMENDED_PLAN, type PricingPlanKey } from './pricingContent';
import {
  PRICING_COPY_SIN_CREDITOS,
  PRICING_NOTA_DEFAULT,
  type PricingContextCopy,
} from './pricingContexts';

/** Contexto de cobro de la empresa, resuelto en una sola consulta. */
interface CobroCtx {
  pais: string;
  plan: string;
  /** Label de `company_profiles.rubro` — elige el carrusel lateral. */
  rubro: string | null;
  plataformaPago: string | null;
  miPlanCustom: MiPlanCustom | null;
  shortName: string;
  companyName: string;
}

export interface CreditsPricingSheetProps {
  /** Cerrar y volver a lo que el usuario estaba haciendo. */
  onClose: () => void;
  /**
   * Título y bajada del catálogo. Por defecto el de "sin créditos", que es el
   * motivo original por el que existe este componente.
   *
   * Se abre también desde botones de mejora deliberada (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo), donde el usuario
   * puede tener el saldo intacto: ahí el copy por defecto afirmaría algo falso.
   */
  copy?: PricingContextCopy;
  /**
   * Texto del botón de salida. Acompaña al copy: "Seguir sin créditos" no tiene
   * sentido para alguien que entró a mirar planes teniendo saldo.
   *
   * `null` = **sin botón de salida**. Se usa en la prueba gratis sin créditos, donde
   * el link prometía una salida que no existe (ADR-2026-08-03-sin-seguir-sin-creditos-en-prueba-gratis).
   * El catálogo igual se cierra con la X de arriba y, en mobile, arrastrándolo.
   */
  skipLabel?: string | null;
  /**
   * Nota al pie, debajo del botón. Cambia con el contexto igual que el copy: a quien
   * está en la prueba gratis no se le habla de "mejorar tu plan"
   * (ADR-2026-08-03-copy-prueba-gratis-suscribirse).
   */
  nota?: string;
  /**
   * Cómo se presenta — ADR-2026-07-31-tarjeta-cuenta-entera-lleva-perfil.
   *
   * `overlay` (default) es para lo que este componente fue hecho: aparecer sobre
   * la pantalla que el usuario está mirando. `embebido` lo monta **en el flujo**
   * de quien lo llama, sin portal ni scrim; lo usa Configuración → Plan, donde
   * los planes son parte de la pantalla.
   *
   * Lo que NO cambia con esto son las ramas de cobro de arriba, que es justamente
   * el motivo de reusar este componente en vez de armar un catálogo aparte.
   */
  presentacion?: 'overlay' | 'embebido';
}

export function CreditsPricingSheet({
  onClose,
  copy = PRICING_COPY_SIN_CREDITOS,
  skipLabel = 'Seguir sin créditos',
  nota = PRICING_NOTA_DEFAULT,
  presentacion = 'overlay',
}: CreditsPricingSheetProps) {
  const embebido = presentacion === 'embebido';
  const navigate = useNavigate();
  const [estado, setEstado] = useState<'cargando' | 'ok' | 'error'>('cargando');
  const [ctx, setCtx] = useState<CobroCtx | null>(null);
  const [coupon, setCoupon] = useState<AppliedCoupon | null>(null);
  const [selected, setSelected] = useState<PricingPlanKey>(RECOMMENDED_PLAN);
  const [tierIndex, setTierIndex] = useState(0);
  const [annual, setAnnual] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Cancelación explícita: si el usuario cierra y vuelve a abrir rápido, la
    // consulta vieja no tiene que pisar a la nueva (mismo motivo que ADR-670).
    let cancelado = false;
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (cancelado) return;
        if (!user) {
          setEstado('error');
          return;
        }
        // `error` explícito en las DOS consultas — ADR-2026-07-31-fail-closed-real-catalogo-enlaces.
        //
        // `.single()` NO tira: ante un fallo devuelve `{ data: null, error }`, así que
        // el `catch` de abajo no se entera. La de `profiles` zafaba de casualidad
        // —`data: null` hacía saltar el guard de `id_company`—, pero la de
        // `company_profiles` seguía de largo con `company === null` y rellenaba los
        // campos con defaults de plan gratis, llegando a `setEstado('ok')`.
        //
        // Eso rompía las tres ramas que este componente documenta como innegociables:
        //  · `plan_custom_id` indefinido → un cliente de plan a medida veía el
        //    catálogo público (ADR-664).
        //  · `plataforma_pago` en `null` → un cliente de Stripe con suscripción
        //    activa iba al Checkout en vez del Billing Portal, creando una SEGUNDA
        //    suscripción con la vieja todavía cobrando (ADR-661).
        //  · `plan` en `'gratis'` → el catálogo marcaba mal el plan vigente.
        //
        // Falla cerrado: el efecto de `estado === 'error'` manda a `/subscription`,
        // que resuelve todo esto en su propia consulta (ADR-668).
        const { data: profile, error: errorPerfil } = await supabase
          .from('profiles')
          .select('id_company, name_short')
          .eq('id', user.id)
          .single();
        if (cancelado) return;
        if (errorPerfil || !profile?.id_company) {
          setEstado('error');
          return;
        }
        const { data: company, error: errorEmpresa } = await supabase
          .from('company_profiles')
          .select('nombre, pais, plan, plataforma_pago, plan_custom_id, rubro')
          .eq('id_company', profile.id_company)
          .single();
        if (cancelado) return;
        if (errorEmpresa || !company) {
          setEstado('error');
          return;
        }
        const planCustomId = (company as any)?.plan_custom_id as
          | string
          | null
          | undefined;
        // `fetchDatosPlanCustom` sólo trae el texto y falla cerrado; el
        // discriminador es `plan_custom_id` de esta misma consulta.
        const custom = planCustomId
          ? await fetchDatosPlanCustom(planCustomId)
          : null;
        if (cancelado) return;
        setCtx({
          pais: ((company as any)?.pais as string) || '',
          plan: ((company as any)?.plan as string) || 'gratis',
          plataformaPago: ((company as any)?.plataforma_pago as string) || null,
          rubro: ((company as any)?.rubro as string) || null,
          miPlanCustom: custom,
          shortName: ((profile as any)?.name_short as string) || '',
          companyName: ((company as any)?.nombre as string) || '',
        });
        setEstado('ok');
      } catch {
        if (!cancelado) setEstado('error');
      }
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  // El cupón va aparte del contexto: si falla, el catálogo se muestra igual a
  // precio de lista en vez de quedarse trabado.
  useEffect(() => {
    let cancelado = false;
    fetchHotSaleCoupon().then((c) => {
      if (!cancelado) setCoupon(c);
    });
    return () => {
      cancelado = true;
    };
  }, []);

  // Falla cerrado: sin poder confirmar si es cliente de plan a medida, no se le
  // muestra el catálogo público. `/subscription` lo resuelve por su cuenta.
  useEffect(() => {
    if (estado !== 'error') return;
    navigate('/subscription');
    onClose();
  }, [estado, navigate, onClose]);

  const pais = ctx?.pais ?? '';
  const currentPlan = ctx?.plan ?? 'gratis';
  const multiplicador = coupon?.multiplicador ?? null;

  const plans = useMemo(
    () =>
      buildPlanViews({
        currentPlan,
        ultraTiers: ULTRA_TIERS,
        ultraTierIndex: tierIndex,
        coupon: multiplicador ? { multiplicador } : null,
        arsBaseFor: (key) => getPlanById(key)?.price ?? 0,
        getPlanPrice: (planId, ars) => getPlanPriceForPais(planId, ars, pais),
        formatPrice: (amount) => formatPriceForPais(amount, pais),
        planOrder: PLAN_ORDER,
        processing,
        onCancel: () => {},
        // La baja NO se ofrece en este contexto: el usuario vino a destrabar
        // créditos, no a cancelar. `buildPlanViews` la pone en la card del plan
        // vigente, así que se saca acá — sigue estando en `/subscription`, que es
        // su única entrada canónica.
      }).map((p) => ({ ...p, secondaryLabel: undefined, onSecondary: undefined })),
    [currentPlan, tierIndex, multiplicador, pais, processing],
  );

  /** Misma ramificación que `Paywall.handleSubscription` (ADR-661). */
  const handleSubscribe = async (key: PricingPlanKey) => {
    try {
      setProcessing(true);
      setError(null);
      const planId = key === 'enterprise' ? ULTRA_TIERS[tierIndex].planId : key;

      if (
        ctx?.plataformaPago === 'stripe' &&
        currentPlan &&
        currentPlan !== 'gratis'
      ) {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error('Usuario no encontrado');
        // El spinner queda puesto a propósito: el browser se está yendo.
        window.location.href = await fetchStripeBillingPortalUrl(user.id);
        return;
      }

      navigate('/checkout', {
        state: {
          planId,
          pais,
          coupon: coupon || undefined,
          currentPlan,
        },
      });
      onClose();
      setProcessing(false);
    } catch (err: any) {
      setError(err?.message || 'Error al procesar la suscripción');
      setProcessing(false);
    }
  };

  // Mientras carga no se pinta nada: con `plan_custom_id` sin resolver, el
  // catálogo le ofrecería un plan público a un cliente a medida (ADR-668).
  if (estado !== 'ok' || !ctx) return null;

  // Plan a medida: ni catálogo ni portal — los créditos se negocian (ADR-664).
  if (ctx.miPlanCustom) {
    const aviso = <PlanCustomSinCreditos ctx={ctx} onClose={onClose} embebido={embebido} />;
    return embebido ? aviso : createPortal(aviso, document.body);
  }

  const catalogo = (
    <PricingCatalog
      variant={embebido ? 'embedded' : 'sheet'}
      // Como overlay aparece sobre una pantalla que se queda donde está y por eso
      // se desvanece al entrar (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo). Embebido no: ya está dentro de una
      // pantalla que hace su propio fade, y anidarlos oscurece y demora.
      animateIn={!embebido}
      contextCopy={copy}
      plans={plans}
      selected={selected}
      onSelect={setSelected}
      onSubscribe={handleSubscribe}
      processing={processing}
      onClose={onClose}
      skipLabel={skipLabel ?? undefined}
      onSkip={skipLabel === null ? undefined : onClose}
      ultraTier={{
        index: tierIndex,
        count: ULTRA_TIERS.length,
        onChange: setTierIndex,
      }}
      showBillingToggle={false}
      annual={annual}
      onAnnualChange={setAnnual}
      error={error}
      // Carrusel lateral con las slides del rubro de la empresa; sin rubro conocido,
      // el set general (ADR-2026-08-03-catalogo-planes-usa-slides-del-rubro).
      slides={getCarouselSlidesPorLabel(ctx?.rubro)}
      note={<p className="max-w-xl">{nota}</p>}
    />
  );

  // Embebido va en el flujo de quien lo llama. Como overlay va por portal a
  // `body`: el shell de la app es `fixed` + `transform-gpu`, y un `fixed` dentro
  // de un ancestro con transform se posiciona respecto de ÉL (mismo motivo que en
  // `Paywall`, ADR-2026-07-28-chrome-paywall-va-anclado-overlay y ADR-2026-07-29-catalogo-paywall-modales-comparten-z).
  return embebido ? catalogo : createPortal(catalogo, document.body);
}

/**
 * Salida para clientes con plan a medida: hablar con Atlas. Conserva el copy y el
 * CTA que tenía `SubscriptionLimitModal` para este caso.
 */
function PlanCustomSinCreditos({
  ctx,
  onClose,
  embebido = false,
}: {
  embebido?: boolean;
  ctx: CobroCtx;
  onClose: () => void;
}) {
  return (
    <div
      className={
        embebido
          ? 'w-full'
          : 'fixed inset-0 z-[9999] flex items-center justify-center bg-brand-black/60 p-4 backdrop-blur-sm'
      }
    >
      <div
        className={`w-full max-w-xl rounded-lg bg-surface-card ${
          embebido ? 'border border-neutral-200' : 'shadow-md'
        }`}
      >
        <div className="p-5">
          <h3 className="mb-2 text-center text-[17px] font-semibold text-brand-black/90 sm:text-left sm:text-[19px]">
            Te quedaste sin créditos
          </h3>
          <p className="mb-6 text-center text-[15px] text-brand-black/90 sm:text-left">
            Tu {ctx.miPlanCustom?.nombre} es un plan a medida, así que los créditos
            los sumamos con vos. Escribinos y lo resolvemos.
          </p>
          <div className="flex flex-col items-center justify-center gap-2.5 sm:flex-row sm:justify-end">
            <button
              onClick={onClose}
              className="inline-flex h-10 w-full items-center justify-center rounded-md border border-neutral-200 bg-surface-card px-4 text-center text-[15px] font-medium text-brand-black/90 shadow-sm transition-colors hover:bg-neutral-100 sm:w-auto"
            >
              Cerrar
            </button>
            <ContactarPorWhatsApp
              mensaje={`Buenas, soy ${ctx.shortName || ''} de ${ctx.companyName || ''}. Me quedé sin créditos de mi ${ctx.miPlanCustom?.nombre} y quería ver cómo sumar más.`}
              tituloQR="Hablemos de tu plan"
            >
              Pedir más créditos
            </ContactarPorWhatsApp>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreditsPricingSheet;
